import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MessageService, ConfirmationService } from 'primeng/api';
import { Component, OnInit, Input, EventEmitter, Output, Inject } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { AlertService } from '@app/_services';
import { PrimeNGConfig } from 'primeng/api';
import { DataReportService } from '../data-report.service';
import { DataReport } from '../data-report';
import { ColumnApi, GridApi, GridReadyEvent } from 'ag-grid-community';
import { PropertyService } from '@app/_components/property';

@Component({
    selector: 'app-data-report',
    templateUrl: './data-report.component.html',
    styleUrls: ['./data-report.component.css'],
    providers: [MessageService, ConfirmationService],
    standalone: false
})
export class DataReportComponent implements OnInit {

    developments: any;
	rowData: any = [];
	gridApi!: GridApi;
  	gridColumnApi!: ColumnApi;

	qualityOptions = [
		{ label: 'Listing Quality', value: 'listing' },
		{ label: 'Rates Quality', value: 'rates' },
		{ label: 'Fees and Tax Quality', value: 'fees_tax' }
	];

	referenceIdStringMappings = {
		"SEND_HOLD_EXPIRY_MAIL_JOB": "Expired Hold Reminder",
		"AutoExpireHoldsJob": "Hold Expiry Confirmation",
		"AutoHoldReminderJob": "Upcoming Hold Expiry Reminder",
		"GUEST_REVIEW_BEFORE_CHECKOUT_DATA_REPORT": "Guest Review Follow Up (Before Checkout)",
		"GUEST_REVIEW_2ND_ATTEMPT_DATA_REPORT": "Guest Review Follow Up (Yesterday's Checkout)",
		"GUEST_REVIEW_3RD_ATTEMPT_DATA_REPORT": "Guest Review Follow Up (3 Days After Checkout)",
		"GUEST_REVIEW_4TH_ATTEMPT_DATA_REPORT": "Guest Review Follow Up (7 Days After Checkout)",

		// VRBO Daily Sync
		"VrboPropertySync": "VRBO Property Sync",
		"VrboLodgingRateSync": "VRBO Lodging Rate Sync",
		"VrboUnitAvailabilitySync": "VRBO Unit Availability Sync",
		"VRBO_CREATE_PROPERTY_IMAGES": "VRBO Create Property Images",
		"VRBO_CREATE_BOOKING_FROM_REQUEST": "VRBO Create Booking from Request",

		// Google Daily Sync
		"GoogleUnitAvailabilitySync": "Google Unit Availability Sync",
		"GoogleInvCountSync": "Google Inv Count Sync",
		"GoogleLosRateSync": "Google LOS Rate Sync",
		"GoogleTaxFeeInfoSync": "Google Tax & Fee Sync",
		"GoogleVrFeedUpload": "Google VR Feed Upload"
	};

	subCategoryStringMappings = {
		"HOLD_EXPIRATION" : "Hold Expiration",
		"GUEST_REVIEW": "Guest Review",
		"VRBO_DAILY_SYNC": "VRBO Daily Sync",
		"GOOGLE_DAILY_SYNC": "Google Daily Sync"
	};

	selectedQualities: string[] = [];
	properties: any[] = [];
	selectedProperties: number[] = [];

	// Dialog state
	displayDialog: boolean = false;
	selectedRowData: any = null;
	metadataExpanded: boolean = false;
	formattedMetadataForDialog: string = '';
	hasMetadataForDialog: boolean = false;

	dayOptions = [
		{ label: 'Today', value: 'today' },
		{ label: 'Yesterday', value: 'yesterday' },
		{ label: 'Last 7 Days', value: 'last7Days' },
		{ label: 'Last 30 Days', value: 'last30Days' }
	];

	selectedDateRanges: any = {};

	columnDefs = [
		{ field: 'shortTermRentalId', headerName: 'Rental ID', filter: 'agNumberColumnFilter' },
		{ field: 'reservationId', headerName: 'Reservation ID', filter: 'agNumberColumnFilter' },
		{ field: 'propertyCode', headerName: 'Property', filter: 'agTextColumnFilter' },
		{
			field: 'createDate',
			headerName: 'Date',
			filter: 'agDateColumnFilter',
			valueFormatter: (params: any) => {
				if (!params.value) return '';
				const date = new Date(params.value);
				return date.toLocaleString(); // auto formats to user’s local time
			},
			filterParams: {
				comparator: (filterLocalDateAtMidnight: Date, cellValue: string) => {
					if (!cellValue) return -1;
					const cellDate = new Date(cellValue);
					// Remove time for comparison
					const cellDateNoTime = new Date(cellDate.getFullYear(), cellDate.getMonth(), cellDate.getDate());
					if (cellDateNoTime < filterLocalDateAtMidnight) return -1;
					if (cellDateNoTime > filterLocalDateAtMidnight) return 1;
					return 0;
				}
			}
		},
		{
			field: 'referenceId',
			headerName: 'Job Type',
			filter: 'agSetColumnFilter',
			valueFormatter: (params: any) => {
				if (!params.value) return '';
				return this.referenceIdStringMappings[params.value] || params.value;
			},
			keyCreator: (params: any) => {
				return this.referenceIdStringMappings[params.value] || params.value;
			},
			filterParams: {
				values: (params: any) => {
					// ✅ Always take from full dataset, not filtered view
					const allRows = this.rowData || [];
					const uniqueReferenceIds = Array.from(
						new Set(allRows.map((row: any) => row.referenceId))
					);
					const mappedValues = uniqueReferenceIds.map(
						(id: string) => this.referenceIdStringMappings[id] || id
					);
					params.success(mappedValues);
				}
			}
		},
		{
			field: 'testCategoryName',
			headerName: 'Category',
			valueFormatter: (params: any) => {
				if (!params.value) return '';
				return params.value.charAt(0).toUpperCase() + params.value.slice(1).toLowerCase();
			},
			keyCreator: (params: any) => {
				return params.value.charAt(0).toUpperCase() + params.value.slice(1).toLowerCase();
			}
		},
		{
			field: 'testSubCategoryName', headerName: 'Sub Category',
			valueFormatter: (params: any) => {
				if (!params.value) return '';
				return this.subCategoryStringMappings[params.value] || params.value;
			},
			keyCreator: (params: any) => {
				return this.subCategoryStringMappings[params.value] || params.value;
			}
		 },
		{ field: 'testName', headerName: 'Test Name' },
		{ field: 'testDesc', headerName: 'Description' },
		{ field: 'testExpectedResult', headerName: 'Expected', width: 100 },
		{ field: 'testActualResult', headerName: 'Actual', width: 80 },
		{ field: 'deviation', headerName: 'Deviation', filter: 'agNumberColumnFilter', width: 100 },
    {
      field: 'testResultStatus',
      headerName: 'Status',
      cellRenderer: (params: any) => {
        // QualityTest stores testResultStatus as a String (was an int on DataReport).
        const statusMap: { [key: string]: { text: string; color: string } } = {
          'PASS':        { text: 'Pass',        color: 'green' },
          'WARN':        { text: 'Warn',        color: 'orange' },
          'FAIL':        { text: 'Fail',        color: 'red' },
          'INFO':        { text: 'Info',        color: 'blue' },
          'IN_PROGRESS': { text: 'In Progress', color: 'blue' }
        };
        const status = statusMap[params.value];
        return status ? `<span style="color:${status.color}; font-weight:bold;">${status.text}</span>` : 'Unknown';
      },
      valueFormatter: (params: any) => {
        const statusTextMap: { [key: string]: string } = {
          'PASS': 'Pass', 'WARN': 'Warn', 'FAIL': 'Fail', 'INFO': 'Info', 'IN_PROGRESS': 'In Progress'
        };
        return statusTextMap[params.value] || 'Unknown';
      },
      filter: 'agSetColumnFilter',
	  width: 100,
      keyCreator: (params: any) => {
        const statusTextMap: { [key: string]: string } = {
          'PASS': 'Pass', 'WARN': 'Warn', 'FAIL': 'Fail', 'INFO': 'Info', 'IN_PROGRESS': 'In Progress'
        };
        return statusTextMap[params.value] || 'Unknown';
      }
    },
		{ field: 'testResultDesc', headerName: 'Result Desc',},
		{
			field: 'testResultUrl',
			headerName: 'Result',
			cellRenderer: (params: any) => {
				if (!params.value) return '';

				try {
					const obj = typeof params.value === 'string' ? JSON.parse(params.value) : params.value;

					return Object.entries(obj)
						.map(([key, value]) => {
							const isLink = typeof value === 'string' && /^https?:\/\//.test(value);

						if (key === 'INBOX' && isLink) {
							return `<a href="${value}" target="_blank" onclick="event.stopPropagation();" onmousedown="event.stopPropagation();" style="color:#007bff; text-decoration:underline; font-weight:bold;">View Inbox</a>`;
						} else if (key === 'SUCCESS' && isLink) {
							return `<a href="${value}" target="_blank" onclick="event.stopPropagation();" onmousedown="event.stopPropagation();" style="color:green; text-decoration:underline; font-weight:bold;">Success Logs</a>`;
						} else if (key === 'FAILURE' && isLink) {
							return `<a href="${value}" target="_blank" onclick="event.stopPropagation();" onmousedown="event.stopPropagation();" style="color:red; text-decoration:underline; font-weight:bold;">Failure Logs</a>`;
							} else {
							const displayValue = isLink
								? `<a href="${value}" target="_blank" onclick="event.stopPropagation();" onmousedown="event.stopPropagation();" style="color:#007bff; text-decoration:underline;">${value}</a>`
									: `${value}`;
								return `<strong class="text-capitalize">${key}</strong> : ${displayValue}`;
							}
						})
						.join(' , ');
				} catch (e) {
					console.error('Invalid JSON:', e);
					return '';
				}
			}
		},
		{
			field: 'metadata',
			headerName: 'Metadata',
			cellStyle: { 'white-space': 'nowrap' },
			suppressSizeToFit: true,
			autoHeight: true,
			cellRenderer: (params: any) => {
				if (!params.value) return '';

				try {
				const metadata = JSON.parse(params.value);
				if (metadata.reviewURL && Array.isArray(metadata.reviewURL)) {
					return metadata.reviewURL
            .map((item: any) => {
              return Object.entries(item)
                .map(([key, value]) => {
                  // Check if value is a string and looks like a URL
                  const isUrl = typeof value === 'string' && /^(https?:\/\/|www\.)/.test(value);
					  if (isUrl) {
						return `<strong>${key}:</strong> <a href="${value}" target="_blank" onclick="event.stopPropagation();" onmousedown="event.stopPropagation();" style="color:#007bff; text-decoration:underline;">${value}</a>`;
                  } else {
                    return `<strong>${key}:</strong> ${value}`;
                  }
                })
                .join(' , ');
            })
					.join('<br/>'); // each object gets stacked
				}
				return '';
				} catch (e) {
					return params.value;
				}
			}
		},
		{ field: 'adrDevelopmentName', headerName: 'Development Name' },
		{ field: 'developmentId', headerName: 'Development ID', filter: 'agNumberColumnFilter' },
		{ field: 'featureId', headerName: 'Feature ID', filter: 'agNumberColumnFilter' },
	];

	defaultColDef = {
		resizable: true,
		sortable: true,
		filter: 'agSetColumnFilter',  // Excel-style with checkboxes
		floatingFilter: true
	};

	statusMap: { [key: number]: string } = {
		1: 'Pass',
		2: 'Warn',
		3: 'Fail',
		4: 'Info'
	};

	analyticsData: any;

	// Reference IDs that belong to VRBO Daily Sync subcategory
	private vrboDailySyncRefIds: Set<string> = new Set([
		'VrboPropertySync',
		'VrboLodgingRateSync',
		'VrboUnitAvailabilitySync',
		'VRBO_CREATE_PROPERTY_IMAGES',
		'VRBO_CREATE_BOOKING_FROM_REQUEST'
	]);

	private googleDailySyncRefIds: Set<string> = new Set([
		'GoogleUnitAvailabilitySync',
		'GoogleInvCountSync',
		'GoogleLosRateSync',
		'GoogleTaxFeeInfoSync',
		'GoogleVrFeedUpload'
	]);

	constructor(
		private primengConfig: PrimeNGConfig,
		private dataReportService: DataReportService,
		private alertService: AlertService,
		private messageService: MessageService,
		private propertyService: PropertyService,
		private confirmationService: ConfirmationService,
		private formBuilder: UntypedFormBuilder,
		@Inject(MAT_DIALOG_DATA) data: any) {

	}

	@Input() dataReport: DataReport;

	// data
	qualityTestId: number;

	formLoading = false;
	formAlert: String;

	// Quartz Jobs analytics state
	quartzAnalytics: {
		days: Array<{ dateStr: string, label: string }>,
		jobNames: string[],
		values: { [jobName: string]: { [dateStr: string]: { expected: number, actual: number } } },
		todayTimes: { [jobName: string]: { prevFireTime?: string, nextFireTime?: string } },
		todayStatus: { [jobName: string]: string },
		todayStr: string
	} | null = null;
	quartzActionLoadingByJob: { [jobName: string]: boolean } = {};

	ngOnInit() {
		this.loadReports();
		// load properties
		this.fetchProperties();
		// load quartz jobs table
		this.loadQuartzJobsTable();
	}

	// loadReports() {
	// 	 this.formLoading = true;
	// 	this.dataReportService.getReports().subscribe({
	// 		next: (res: any) => {
	// 			this.rowData = res.data.dataReports;
	// 			this.formLoading = false;
	// 		},
	// 		error: (err) => {
	// 			this.formLoading = false;
	// 			this.alertService.error(err);
	// 		}
	// 	});
	// }

	loadReports() {
		 this.formLoading = true;
		this.dataReportService.getReports({
				page: 0,
				size: 1000000,
				sort: 'createDate,desc'
			}).subscribe({
			next: (res: any) => {
				this.rowData = res.data.qualityTests || res.data.dataReports;

				// Normalize/add subcategory for VRBO jobs so analytics can group them
				this.applySubcategoryOverrides();

					this.formLoading = false;
					this.analyticsData = this.getAnalyticsData();
			},
			error: (err) => {
				this.formLoading = false;
				this.alertService.error(err);
			}
		});
	}

	private getDateStringLocal(date: Date): string {
		const year = date.getFullYear();
		const month = String(date.getMonth() + 1).padStart(2, '0');
		const day = String(date.getDate()).padStart(2, '0');
		return `${year}-${month}-${day}`;
	}

	private getDateLabelFromDate(date: Date): string {
		const dayNum = date.getDate();
		const monthShort = date.toLocaleString('en-US', { month: 'short' });
		return `${dayNum}-${monthShort}`;
	}

	// Load Quartz Jobs status (last 3 days)
		loadQuartzJobsTable(): void {
		this.dataReportService.fetchQuartzJobDataAll().subscribe({
			next: (res: any) => {
				const reports = res?.data?.qualityTests || res?.data?.dataReports || res?.dataReports || [];

				// Build last 3 days (today and previous 2)
				const today = new Date();
				const todayStrLocal = this.getDateStringLocal(today);
				const days: Array<{ dateStr: string, label: string }> = [];
				for (let i = 0; i < 3; i++) {
					const d = new Date();
					d.setDate(today.getDate() - i);
					days.push({
						dateStr: this.getDateStringLocal(d),
						label: this.getDateLabelFromDate(d)
					});
				}

				// Aggregate by jobName (testName) and day
				const values: { [jobName: string]: { [dateStr: string]: { expected: number, actual: number } } } = {};
				const todayTimesLatest: { [jobName: string]: { prevFireTime?: string, nextFireTime?: string, lastUpdate?: number } } = {};
				const todayStatusLatest: { [jobName: string]: { status?: string, lastUpdate?: number } } = {};
				const jobSet: Set<string> = new Set();

				reports.forEach((r: any) => {
					const jobName = r?.testName;
					const dateStr = this.getDateStringLocal(new Date(r?.createDate));
					if (!jobName || !dateStr) return;

					// only consider last 3 days
					if (!days.some(d => d.dateStr === dateStr)) return;

					jobSet.add(jobName);
					if (!values[jobName]) values[jobName] = {};
					if (!values[jobName][dateStr]) values[jobName][dateStr] = { expected: 0, actual: 0 };

					const expected = Number(r?.testExpectedResult ?? 0);
					const actual = Number(r?.testActualResult ?? 0);

					values[jobName][dateStr].expected += isNaN(expected) ? 0 : expected;
					values[jobName][dateStr].actual += isNaN(actual) ? 0 : actual;

					// Capture today's prev/next fire times from metadata (latest lastUpdate wins)
					if (dateStr === todayStrLocal && r?.metadata) {
						let meta: any = r.metadata;
						try {
							if (typeof meta === 'string') meta = JSON.parse(meta);
						} catch {}
						const prevFireTime = meta?.prevFireTime;
						const nextFireTime = meta?.nextFireTime;
						const status = meta?.status;
						if (prevFireTime || nextFireTime) {
							const lu = r?.lastUpdate ? Date.parse(r.lastUpdate) : 0;
							const existing = todayTimesLatest[jobName]?.lastUpdate ?? -1;
							if (lu >= existing) {
								todayTimesLatest[jobName] = {
									prevFireTime,
									nextFireTime,
									lastUpdate: lu
								};
							}
						}
						if (status) {
							const lu = r?.lastUpdate ? Date.parse(r.lastUpdate) : 0;
							const existing = todayStatusLatest[jobName]?.lastUpdate ?? -1;
							if (lu >= existing) {
                                todayStatusLatest[jobName] = { status, lastUpdate: lu };
							}
						}
					}
				});

				const jobNames = Array.from(jobSet).sort((a, b) => a.localeCompare(b));

				const todayTimes: { [jobName: string]: { prevFireTime?: string, nextFireTime?: string } } = {};
				const todayStatus: { [jobName: string]: string } = {};
				jobNames.forEach((jn) => {
					if (todayTimesLatest[jn]) {
						todayTimes[jn] = {
							prevFireTime: todayTimesLatest[jn].prevFireTime,
							nextFireTime: todayTimesLatest[jn].nextFireTime
						};
					}
					if (todayStatusLatest[jn]?.status) {
						todayStatus[jn] = todayStatusLatest[jn].status as string;
					}
				});

				this.quartzAnalytics = {
					days,
					jobNames,
					values,
					todayTimes,
					todayStatus,
					todayStr: todayStrLocal
				};
			},
			error: (err) => {
				// Non-fatal
				console.error('Failed to load Quartz Jobs data', err);
			}
		});
	}

	isAnyQuartzActionLoading(jobName: string): boolean {
		return !!this.quartzActionLoadingByJob[jobName];
	}

	triggerQuartzJobNow(jobName: string): void {
		this.executeQuartzAction(
			jobName,
			'Trigger Now',
			`Trigger ${jobName} now?`,
			() => this.dataReportService.triggerQuartzJobNowRpt(jobName)
		);
	}

	resetQuartzTriggerState(jobName: string): void {
		this.executeQuartzAction(
			jobName,
			'Reset Trigger State',
			`Reset trigger state for ${jobName}?`,
			() => this.dataReportService.resetQuartzTriggerStateRpt(jobName)
		);
	}

	recoverQuartzStaleStarted(jobName: string): void {
		this.executeQuartzAction(
			jobName,
			'Recover Stale Job',
			`Recover stale STARTED state for ${jobName}?`,
			() => this.dataReportService.recoverQuartzStaleStartedRpt(jobName)
		);
	}

	private executeQuartzAction(
		jobName: string,
		actionLabel: string,
		confirmMessage: string,
		requestFactory: () => any
	): void {
		if (this.isAnyQuartzActionLoading(jobName)) return;

		this.confirmationService.confirm({
			header: actionLabel,
			message: confirmMessage,
			icon: 'pi pi-exclamation-triangle',
			accept: () => {
				this.quartzActionLoadingByJob[jobName] = true;
				requestFactory().subscribe({
					next: (res: any) => {
						this.messageService.add({
							severity: 'success',
							summary: actionLabel,
							detail: res?.message || `${actionLabel} completed successfully`
						});
						this.loadQuartzJobsTable();
						this.loadReports();
					},
					error: (err: any) => {
						this.messageService.add({
							severity: 'error',
							summary: actionLabel,
							detail: err?.error?.message || err?.message || `${actionLabel} failed`
						});
					},
					complete: () => {
						this.quartzActionLoadingByJob[jobName] = false;
					}
				});
			},
			reject: () => {
				// No-op
			}
		});
	}


	// Ensure rows with VRBO reference IDs are grouped under VRBO_DAILY_SYNC
	private applySubcategoryOverrides(): void {
		if (!Array.isArray(this.rowData)) return;
		this.rowData.forEach((row: any) => {
			const refId = row?.referenceId;
			if (refId && this.vrboDailySyncRefIds.has(refId)) {
				row.testSubCategoryName = 'VRBO_DAILY_SYNC';
			} else if (refId && this.googleDailySyncRefIds.has(refId)) {
				row.testSubCategoryName = 'GOOGLE_DAILY_SYNC';
			}
		});
	}

	// Format ISO string to dd-MMM HH:mm:ss local time
	formatTime(timeIso?: string): string {
		if (!timeIso) return '-';
		try {
			const d = new Date(timeIso);
			const day = String(d.getDate()).padStart(2, '0');
			const monthShort = d.toLocaleString('en-US', { month: 'short' });
			const hours = String(d.getHours()).padStart(2, '0');
			const minutes = String(d.getMinutes()).padStart(2, '0');
			const seconds = String(d.getSeconds()).padStart(2, '0');
			return `${day}-${monthShort} ${hours}:${minutes}:${seconds}`;
		} catch {
			return '-';
		}
	}

	// Choose bg class for Quartz cell, overriding to green when status is IN_PROGRESS for today's run
	getQuartzCellBgClass(jobName: string, dateStr: string, data: any): string {
		try {
			if (this.quartzAnalytics && dateStr === this.quartzAnalytics.todayStr) {
				const status = this.quartzAnalytics.todayStatus?.[jobName];
				if (status === 'IN_PROGRESS') {
					return 'bg-analytics-success';
				}
			}
		} catch {}
		return this.getBgClass(data);
	}

	onAnalyticsClick(day: 'today' | 'yesterday' | 'last30Days' | 'last7Days', subCategory: string, refId: string): void {
		if (!this.gridApi) return;

		this.gridApi.setFilterModel({});

		const subCategoryDisplay = this.subCategoryStringMappings[subCategory] || subCategory;
		const refIdDisplay = this.referenceIdStringMappings[refId] || refId;

		const today = new Date();
		const yesterday = new Date();
		yesterday.setDate(today.getDate() - 1);

		let filterModel: any = {
			testSubCategoryName: {
				filterType: 'set',
				values: [subCategoryDisplay]
			},
			referenceId: {
				filterType: 'set',
				values: [refIdDisplay]
			}
		};

		if (day === 'last30Days') {
			// Calculate 30 days ago (including today)
			const startDate = new Date();
			startDate.setDate(today.getDate() - 29);
			const endDate = today;
			const dateFrom = this.formatDateLocal(
				new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate())
			);
			const dateTo = this.formatDateLocal(
				new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate())
			);
			filterModel.createDate = {
				filterType: 'date',
				type: 'inRange',
				dateFrom: dateFrom,
				dateTo: dateTo
			};
		} else if (day === 'last7Days') {
			const startDate = new Date();
			startDate.setDate(today.getDate() - 6);
			const endDate = today;
			const dateFrom = this.formatDateLocal(
				new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate())
			);
			const dateTo = this.formatDateLocal(
				new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate())
			);
			filterModel.createDate = {
				filterType: 'date',
				type: 'inRange',
				dateFrom: dateFrom,
				dateTo: dateTo
			};
		} else {
			const targetDate = day === 'today' ? today : yesterday;
			const dateString = this.formatDateLocal(
				new Date(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate())
			);
			filterModel.createDate = {
				filterType: 'date',
				type: 'equals',
				dateFrom: dateString
			};
		}

		this.gridApi.setFilterModel(filterModel);
		this.gridApi.onFilterChanged();
		try { this.gridApi.ensureIndexVisible(0, 'top'); } catch {}
		this.scrollToGrid();
	}

	private formatDateLocal(date: Date): string {
		const year = date.getFullYear();
		const month = String(date.getMonth() + 1).padStart(2, '0');
		const day = String(date.getDate()).padStart(2, '0');
		return `${year}-${month}-${day}`;
	}





	fetchProperties() {
		if (!this.properties || this.properties === undefined || this.properties.length < 1) {
			try {
				this.propertyService.findAllSummary()
					.subscribe(
						res => {
							this.properties = res.data.properties.map((p: any) => ({
								id: p.shortTermRentalId,
								name: p.propertyCode
							}));
						},
						err => {
							console.log('Feature-Update FAIL: ' + JSON.stringify(err));
							this.alertService.error(err);
						},
						() => { });
			} catch (error) {
				console.log('Form Invalid: ' + JSON.stringify(error));
				this.alertService.error(error);
			}
		}
		else {
			// console.log('properties are ' + this.properties);
			return (this.properties);
		}
	}

	runQualityTest() {
		console.log("Selected Quality Types: ", this.selectedQualities);
		console.log("Selected Property: ", this.selectedProperties);

		// If none selected, run all
		const qualitiesToRun = this.selectedQualities?.length
			? this.selectedQualities
			: ['listing', 'rates', 'fees_tax'];

		// Example: [ "listing", "rates" ]
		qualitiesToRun.forEach(quality => {
			let endpoint = '';

			switch (quality) {
			case 'listing':
				endpoint = 'listing-quality';
				break;
			case 'rates':
				endpoint = 'rates-quality';
				break;
			case 'fees_tax':
				endpoint = 'fee-and-tax-quality';
				break;
			}

			if (endpoint) {
			// Backend expects list of rental IDs (can pass [] to run for all)
			this.dataReportService.runQuality(endpoint, this.selectedProperties)
				.subscribe({
				next: (res: any) => {
					this.messageService.add({
						severity: 'success',
						summary: 'Success',
						detail: res.message || 'Quality Test executed'
					});
					// Reload reports after successful run
            		this.loadReports();
				},
				error: (err) => {
					this.messageService.add({
					severity: 'error',
					summary: `Error (${quality})`,
					detail: err.error?.message || 'Failed to run test'
					});
				}
				});
			}
		});
	}


	clearAllFilters() {
		if (this.gridApi) {
			this.gridApi.setFilterModel(null);
			this.gridApi.onFilterChanged();
		}
	}


	onGridReady(params: GridReadyEvent) {
		this.gridApi = params.api;
		this.gridColumnApi = params.columnApi;
	}

	statusBar = {
		statusPanels: [
			{ statusPanel: 'agTotalRowCountComponent', align: 'left' },
			{ statusPanel: 'agFilteredRowCountComponent' },
			{ statusPanel: 'agSelectedRowCountComponent' }
		]
	};

	onFirstDataRendered(params: GridReadyEvent) {
		const allColumnIds: string[] = [];
		// params.columnApi.getAllColumns().forEach(col => allColumnIds.push(col.getId()));
		params.columnApi.autoSizeAllColumns(false);
	}

	onRowClicked(event: any) {
		this.selectedRowData = event.data;
		this.formattedMetadataForDialog = this.getFormattedMetadata(this.selectedRowData?.metadata);
		this.hasMetadataForDialog = !!(this.formattedMetadataForDialog && this.formattedMetadataForDialog.trim());
		this.metadataExpanded = this.isQuartzRow(this.selectedRowData);
		this.displayDialog = true;
	}

	closeDialog() {
		this.displayDialog = false;
		this.selectedRowData = null;
		this.formattedMetadataForDialog = '';
		this.hasMetadataForDialog = false;
		this.metadataExpanded = false;
	}

	toggleMetadataCollapse(): void {
		this.metadataExpanded = !this.metadataExpanded;
	}

	private isQuartzRow(row: any): boolean {
		const category = (row?.testCategoryName || '').toString().toUpperCase();
		const referenceId = (row?.referenceId || '').toString().toUpperCase();
		return category === 'QUARTZ_JOBS' || referenceId.startsWith('QUARTZ_JOB_');
	}

	getFormattedResultUrl(resultUrl: any): string {
		if (!resultUrl) return '';

		try {
			const obj = typeof resultUrl === 'string' ? JSON.parse(resultUrl) : resultUrl;

			return Object.entries(obj)
				.map(([key, value]) => {
					const isLink = typeof value === 'string' && /^https?:\/\//.test(value);

					if (key === 'SUCCESS' && isLink) {
						return `<a href="${value}" target="_blank" class="btn btn-success btn-sm me-2 mb-2">
							<i class="pi pi-external-link me-1"></i>View Inbox
						</a>`;
					} else if (key === 'FAILURE' && isLink) {
						return `<a href="${value}" target="_blank" class="btn btn-danger btn-sm me-2 mb-2">
							<i class="pi pi-external-link me-1"></i>View Logs
						</a>`;
					} else {
						const displayValue = isLink
							? `<a href="${value}" target="_blank" class="text-primary">${value}</a>`
							: `${value}`;
						return `<div class="mb-2"><strong class="text-capitalize">${key}:</strong> ${displayValue}</div>`;
					}
				})
				.join('');
		} catch (e) {
			console.error('Invalid JSON:', e);
			return '';
		}
	}

	getFormattedMetadata(metadata: any): string {
		if (!metadata) return '';

		try {
			const metadataObj = typeof metadata === 'string' ? JSON.parse(metadata) : metadata;
			if (!metadataObj || typeof metadataObj !== 'object') return '';

			const preferredOrder = [
				'status',
				'failureReason',
				'possibleStaleStarted',
				'lastKnownStartTime',
				'lastKnownTriggerName',
				'prevFireTime',
				'nextFireTime',
				'totalExpectedCount',
				'totalActualCount',
				'totalFailedCount',
				'highFrequency',
				'warningCode'
			];

			const allKeys = Object.keys(metadataObj);
			const orderedKeys = [
				...preferredOrder.filter(k => allKeys.includes(k)),
				...allKeys.filter(k => !preferredOrder.includes(k))
			];

			const content = orderedKeys
				.map((key: string) => {
					const value = metadataObj[key];
					return this.renderMetadataEntry(key, value);
				})
				.filter(Boolean)
				.join('');

			return content || '';
		} catch (e) {
			if (typeof metadata === 'string') {
				return `<pre class="mb-0" style="white-space:pre-wrap; word-break:break-word;">${metadata}</pre>`;
			}
			return '';
		}
	}

	private renderMetadataEntry(key: string, value: any): string {
		if (value === null || value === undefined || value === '') return '';
		const label = this.toMetadataLabel(key);

		if (Array.isArray(value)) {
			if (value.length === 0) return `<div class="mb-2"><strong>${label}:</strong> []</div>`;
			const items = value.map((item: any) => `<li>${this.renderMetadataValue(item)}</li>`).join('');
			return `<div class="mb-2"><strong>${label}:</strong><ul class="mb-1 ps-3">${items}</ul></div>`;
		}

		if (typeof value === 'object') {
			const nested = Object.entries(value as Record<string, any>)
				.map(([nestedKey, nestedValue]) => `<li><strong>${this.toMetadataLabel(nestedKey)}:</strong> ${this.renderMetadataValue(nestedValue)}</li>`)
				.join('');
			return `<div class="mb-2"><strong>${label}:</strong><ul class="mb-1 ps-3">${nested}</ul></div>`;
		}

		return `<div class="mb-2"><strong>${label}:</strong> ${this.renderMetadataValue(value)}</div>`;
	}

	private renderMetadataValue(value: any): string {
		if (value === null || value === undefined) return 'N/A';

		if (Array.isArray(value)) {
			if (value.length === 0) return '[]';
			return `<ul class="mb-1 ps-3">${value.map(v => `<li>${this.renderMetadataValue(v)}</li>`).join('')}</ul>`;
		}

		if (typeof value === 'object') {
			try {
				return `<pre class="mb-0" style="white-space:pre-wrap; word-break:break-word;">${JSON.stringify(value, null, 2)}</pre>`;
			} catch {
				return String(value);
			}
		}

		const stringValue = String(value);
		const isUrl = /^(https?:\/\/|www\.)/i.test(stringValue);
		if (isUrl) {
			const href = /^https?:\/\//i.test(stringValue) ? stringValue : `https://${stringValue}`;
			return `<a href="${href}" target="_blank" class="text-primary">${stringValue}</a>`;
		}

		return stringValue;
	}

	private toMetadataLabel(key: string): string {
		if (!key) return '';
		return key
			.replace(/([a-z])([A-Z])/g, '$1 $2')
			.replace(/_/g, ' ')
			.replace(/\b\w/g, (char) => char.toUpperCase());
	}

	getAnalyticsData(): any {
		const analyticsData: any = {};
		const today = new Date();
		const yesterday = new Date();
		yesterday.setDate(today.getDate() - 1);

		// yyyy-mm-dd
		const getDateString = (date: Date) =>
			`${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

		const todayStr = getDateString(today);
		const yesterdayStr = getDateString(yesterday);

		// Helper to get all date strings in last 30 days (including today)
		const getLast30DaysStrings = () => {
			const dates: string[] = [];
			for (let i = 0; i < 30; i++) {
				const d = new Date();
				d.setDate(today.getDate() - i);
				dates.push(getDateString(d));
			}
			return dates;
		};
		const last30DaysStrs = getLast30DaysStrings();

		const getLast7DaysStrings = () => {
			const dates: string[] = [];
			for (let i = 0; i < 7; i++) {
				const d = new Date();
				d.setDate(today.getDate() - i);
				dates.push(getDateString(d));
			}
			return dates;
		};
		const last7DaysStrs = getLast7DaysStrings();

		// Helper to format a label for any date (e.g., 5-Jan)
		const getDateLabelFromDate = (date: Date): string => {
			const dayNum = date.getDate();
			const monthShort = date.toLocaleString('en-US', { month: 'short' });
			return `${dayNum}-${monthShort}`;
		};

		// Iterate over subcategories
		Object.keys(this.subCategoryStringMappings).forEach((subCategory: string) => {

			this.selectedDateRanges[subCategory] = ['today'];

			// Filter rows for this subCategory
			const filteredRows = (this.rowData || []).filter(
				(row: any) => row.testSubCategoryName === subCategory
			);

			const groupByDateAndReference = (dateStr: string) => {
				const result: any = {};
				const propertySets: { [key: string]: Set<string | number> } = {};
				const successPropertySets: { [key: string]: Set<string | number> } = {};

				filteredRows.forEach((row: any) => {
					if (!row.createDate) return;
					const rowDate = new Date(row.createDate);
					const rowDateStr = getDateString(rowDate);

					if (rowDateStr === dateStr) {
						const refId = row.referenceId;
						if (!result[refId]) {
							result[refId] = { expected: 0, actual: 0 };
						}

						const expected = Number(row.testExpectedResult ?? 0);
						const actual = Number(row.testActualResult ?? 0);

						result[refId].expected += isNaN(expected) ? 0 : expected;
						result[refId].actual += isNaN(actual) ? 0 : actual;

						// Track unique properties and successes for VrboPropertySync
						if (refId === 'VrboPropertySync') {
							if (!propertySets[refId]) propertySets[refId] = new Set();
							if (!successPropertySets[refId]) successPropertySets[refId] = new Set();
							const propertyKey = row.shortTermRentalId ?? row.propertyCode ?? `${row.shortTermRentalId}`;
							if (propertyKey !== undefined) propertySets[refId].add(propertyKey);
							// Count success if actual > 0 for that property
							if (Number(row.testActualResult ?? 0) > 0 && propertyKey !== undefined) {
								successPropertySets[refId].add(propertyKey);
							}
						}
					}
				});

				// Override with property-based counts for VrboPropertySync
				if (propertySets['VrboPropertySync']) {
					result['VrboPropertySync'] = {
						expected: propertySets['VrboPropertySync'].size || 0,
						actual: successPropertySets['VrboPropertySync']?.size || 0
					};
				}
				return result;
			};

			const groupByLast30DaysAndReference = () => {
				const result: any = {};
				const propertySets: { [key: string]: Set<string | number> } = {};
				const successPropertySets: { [key: string]: Set<string | number> } = {};

				filteredRows.forEach((row: any) => {
					if (!row.createDate) return;
					const rowDate = new Date(row.createDate);
					const rowDateStr = getDateString(rowDate);

					if (last30DaysStrs.includes(rowDateStr)) {
						const refId = row.referenceId;
						if (!result[refId]) {
							result[refId] = { expected: 0, actual: 0 };
						}

						const expected = Number(row.testExpectedResult ?? 0);
						const actual = Number(row.testActualResult ?? 0);

						result[refId].expected += isNaN(expected) ? 0 : expected;
						result[refId].actual += isNaN(actual) ? 0 : actual;

						// Track unique properties and successes for VrboPropertySync across the 30-day window
						if (refId === 'VrboPropertySync') {
							if (!propertySets[refId]) propertySets[refId] = new Set();
							if (!successPropertySets[refId]) successPropertySets[refId] = new Set();
							const propertyKey = row.shortTermRentalId ?? row.propertyCode ?? `${row.shortTermRentalId}`;
							if (propertyKey !== undefined) propertySets[refId].add(propertyKey);
							if (Number(row.testActualResult ?? 0) > 0 && propertyKey !== undefined) {
								successPropertySets[refId].add(propertyKey);
							}
						}
					}
				});

				// Override with property-based counts for VrboPropertySync over the 30-day window
				if (propertySets['VrboPropertySync']) {
					result['VrboPropertySync'] = {
						expected: propertySets['VrboPropertySync'].size || 0,
						actual: successPropertySets['VrboPropertySync']?.size || 0
					};
				}
				return result;
			};

			const groupByLast7DaysAndReference = () => {
				const result: any = {};
				const propertySets: { [key: string]: Set<string | number> } = {};
				const successPropertySets: { [key: string]: Set<string | number> } = {};

				filteredRows.forEach((row: any) => {
					if (!row.createDate) return;
					const rowDate = new Date(row.createDate);
					const rowDateStr = getDateString(rowDate);

					if (last7DaysStrs.includes(rowDateStr)) {
						const refId = row.referenceId;
						if (!result[refId]) {
							result[refId] = { expected: 0, actual: 0 };
						}

						const expected = Number(row.testExpectedResult ?? 0);
						const actual = Number(row.testActualResult ?? 0);

						result[refId].expected += isNaN(expected) ? 0 : expected;
						result[refId].actual += isNaN(actual) ? 0 : actual;

						// Track unique properties and successes for VrboPropertySync across the 7-day window
						if (refId === 'VrboPropertySync') {
							if (!propertySets[refId]) propertySets[refId] = new Set();
							if (!successPropertySets[refId]) successPropertySets[refId] = new Set();
							const propertyKey = row.shortTermRentalId ?? row.propertyCode ?? `${row.shortTermRentalId}`;
							if (propertyKey !== undefined) propertySets[refId].add(propertyKey);
							if (Number(row.testActualResult ?? 0) > 0 && propertyKey !== undefined) {
								successPropertySets[refId].add(propertyKey);
							}
						}
					}
				});

				// Override with property-based counts for VrboPropertySync over the 7-day window
				if (propertySets['VrboPropertySync']) {
					result['VrboPropertySync'] = {
						expected: propertySets['VrboPropertySync'].size || 0,
						actual: successPropertySets['VrboPropertySync']?.size || 0
					};
				}
				return result;
			};

			analyticsData[subCategory] = {
				today: groupByDateAndReference(todayStr),
				yesterday: groupByDateAndReference(yesterdayStr),
				last30Days: groupByLast30DaysAndReference(),
				last7Days: groupByLast7DaysAndReference(),
			};

			// For daily sync subcategories, compute per-day breakdown for last 4 days (today and previous 3 days)
			if (subCategory === 'VRBO_DAILY_SYNC' || subCategory === 'GOOGLE_DAILY_SYNC') {
				const dailyLast4: Array<{ dateStr: string; label: string; data: any }> = [];
				for (let offset = 0; offset < 4; offset++) {
					const d = new Date();
					d.setDate(today.getDate() - offset);
					const dateStr = getDateString(d);
					const label = getDateLabelFromDate(d);
					dailyLast4.push({
						dateStr,
						label,
						data: groupByDateAndReference(dateStr)
					});
				}
				analyticsData[subCategory].dailyLast4 = dailyLast4;
			}
		});

		return analyticsData;
	}

	// --- Analytics table helpers ---
	getDateLabel(day: 'today' | 'yesterday'): string {
		const date = new Date();
		if (day === 'yesterday') {
			date.setDate(date.getDate() - 1);
		}
		const dayNum = date.getDate();
		const monthShort = date.toLocaleString('en-US', { month: 'short' });
		return `${dayNum}-${monthShort}`;
	}

	getBgClass(data: any): string {
		if (!data) {
			return 'bg-analytics-empty';
		}
		const expected = Number(data.expected || 0);
		const actual = Number(data.actual || 0);
		if (expected <= 0) {
			return 'bg-analytics-empty';
		}
		const ratio = actual / expected;
		if (ratio >= 1) return 'bg-analytics-success';
		if (ratio >= 0.75) return 'bg-analytics-warning';
		return 'bg-analytics-danger';
	}

	getOrderedRefs(subCategory: string): string[] {
		// For VRBO, include refs across the last 4 daily buckets so items that
		// exist only on older days still appear in the list.
		let union: string[] = [];
		if ((subCategory === 'VRBO_DAILY_SYNC' || subCategory === 'GOOGLE_DAILY_SYNC') && Array.isArray(this.analyticsData?.[subCategory]?.dailyLast4)) {
			const refs: string[] = [];
			this.analyticsData[subCategory].dailyLast4.forEach((d: any) => {
				refs.push(...Object.keys(d?.data || {}));
			});
			union = Array.from(new Set(refs));
		} else {
			const todayRefs = Object.keys(this.analyticsData?.[subCategory]?.today || {});
			const yestRefs = Object.keys(this.analyticsData?.[subCategory]?.yesterday || {});
			union = Array.from(new Set([...todayRefs, ...yestRefs]));
		}
		// Prefer the display order based on referenceIdStringMappings if available
		const orderIndex: { [key: string]: number } = {};
		Object.keys(this.referenceIdStringMappings).forEach((key, idx) => {
			orderIndex[key] = idx;
		});
		return union.sort((a, b) => {
			const ai = orderIndex[a] ?? 9999;
			const bi = orderIndex[b] ?? 9999;
			if (ai !== bi) return ai - bi;
			return (this.referenceIdStringMappings[a] || a).localeCompare(this.referenceIdStringMappings[b] || b);
		});
	}

	// Whether to show "actual/expected" fraction beneath the number
	shouldShowFraction(refId: string): boolean {
		// Hide for VRBO Create Property Images
		return refId !== 'VRBO_CREATE_PROPERTY_IMAGES';
	}

	// Filter grid to a specific date (YYYY-MM-DD) for VRBO daily cells
	onAnalyticsClickDate(dateStr: string, subCategory: string, refId: string): void {
		if (!this.gridApi) return;

		this.gridApi.setFilterModel({});

		const subCategoryDisplay = this.subCategoryStringMappings[subCategory] || subCategory;
		const refIdDisplay = this.referenceIdStringMappings[refId] || refId;

		const filterModel: any = {
			testSubCategoryName: {
				filterType: 'set',
				values: [subCategoryDisplay]
			},
			referenceId: {
				filterType: 'set',
				values: [refIdDisplay]
			},
			createDate: {
				filterType: 'date',
				type: 'equals',
				dateFrom: dateStr
			}
		};

		this.gridApi.setFilterModel(filterModel);
		this.gridApi.onFilterChanged();
		try { this.gridApi.ensureIndexVisible(0, 'top'); } catch {}
		this.scrollToGrid();
	}

	onQuartzClickDate(dateStr: string, jobName: string): void {
		if (!this.gridApi) return;

		this.gridApi.setFilterModel({});

		const filterModel: any = {
			testName: {
				filterType: 'set',
				values: [jobName]
			},
			createDate: {
				filterType: 'date',
				type: 'equals',
				dateFrom: dateStr
			}
		};

		this.gridApi.setFilterModel(filterModel);
		this.gridApi.onFilterChanged();
		try { this.gridApi.ensureIndexVisible(0, 'top'); } catch {}
		this.scrollToGrid();
	}

	private scrollToGrid(): void {
		// Prefer smooth scroll to the grid container with an offset for headers
		const scrollToEl = (el: HTMLElement) => {
			try {
				const rect = el.getBoundingClientRect();
				const y = rect.top + (window.pageYOffset || document.documentElement.scrollTop) - 80; // header offset
				window.scrollTo({ top: y, behavior: 'smooth' });
				return true;
			} catch { return false; }
		};
		// 1) #data-report-grid
		const container = document.getElementById('data-report-grid');
		if (container && scrollToEl(container)) return;
		// 2) .grid-wrapper
		const wrapper = document.querySelector('.grid-wrapper') as HTMLElement | null;
		if (wrapper && scrollToEl(wrapper)) return;
		// 3) ag-grid-angular element
		const gridEl = document.querySelector('ag-grid-angular') as HTMLElement | null;
		if (gridEl && scrollToEl(gridEl)) return;
		// 4) last resort: bottom
		window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
	}

	// Dialog opens only on row click; no auto-open on analytics clicks




	objectKeys(obj: any): string[] {
		return Object.keys(obj);
	}
}
